import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

/**
 * The legally required privacy controls, rendered inside the app (with the
 * iubenda dashboard's Privacy Widget disabled, this replaces the strip iubenda
 * would otherwise append to document.body — which lands outside #root and
 * breaks the page scroll).
 *
 *  - "Your Privacy Choices" is a BUTTON: it opens the Cookie Solution's
 *    consent-preferences dialog via the documented `_iub.cs.api.openPreferences()`
 *    (must be labeled exactly "Your Privacy Choices" with the opt-out icon for
 *    CCPA/CPRA). The `iubenda-cs-preferences-link` class stays on as a harmless
 *    secondary binding.
 *  - "Notice at Collection" is a plain LINK to the hosted policy document,
 *    which contains the notice — it navigates, so it's an anchor.
 *
 * These controls must be reachable from every page, which is why they're
 * pinned to the bottom of both layouts' NavBars.
 */

const useStyles = makeStyles()(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        // 28px left inset matches the nav items' rail: 16px item margin+padding
        // + 12px icon marginLeft (same line the section subheaders sit on).
        padding: '12px 16px 12px 28px',
        borderTop: '1px solid var(--color-border)',
        flexShrink: 0,
    },
    control: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'var(--font-family)',
        fontSize: 12,
        lineHeight: '16px',
        color: 'var(--color-text-muted)',
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': {
            color: 'var(--color-text)',
            textDecoration: 'underline',
        },
    },
    // Strip native button chrome so the button reads exactly like the link.
    buttonReset: {
        background: 'transparent',
        border: 0,
        padding: 0,
        textAlign: 'left',
    },
}));

// The Notice at Collection is generated as part of the iubenda privacy policy.
// Also the fallback target when the Cookie Solution API is unavailable.
const HOSTED_PRIVACY_POLICY_URL = 'https://www.iubenda.com/privacy-policy/45859185';

// Simplified CCPA/CPRA opt-out icon (blue pill, check + X). Swap for the
// official asset if legal wants pixel-exact artwork.
const OptOutIcon = () => (
    <svg width="26" height="13" viewBox="0 0 30 14" aria-hidden="true" focusable="false">
        <rect x="0.5" y="0.5" width="29" height="13" rx="6.5" fill="#fff" stroke="#0066CC" />
        <path d="M14 0.5h9.5a6.5 6.5 0 0 1 0 13H14" fill="#0066CC" />
        <path d="M5 7.2l2 2 3.5-4" stroke="#0066CC" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19.5 4.5l4 5m0-5l-4 5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const PrivacyChoicesLinks = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();

    const openPrivacyChoices = () => {
        const api = window._iub?.cs?.api;
        if (api?.openPreferences) {
            api.openPreferences();
        } else {
            // Cookie Solution script unavailable (blocked/not yet loaded) - fall back to
            // the hosted policy so the opt-out control is never a dead click.
            window.open(HOSTED_PRIVACY_POLICY_URL, '_blank', 'noopener');
        }
    };

    return (
        <div className={classes.root}>
            <button
                type="button"
                className={cx('iubenda-cs-preferences-link', classes.control, classes.buttonReset)}
                onClick={openPrivacyChoices}
            >
                {t('yourPrivacyChoices')}
                <OptOutIcon />
            </button>
            <a
                href={HOSTED_PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.control}
            >
                {t('noticeAtCollection')}
            </a>
        </div>
    );
};

export default PrivacyChoicesLinks;
